// src/components/Alianzas.jsx
import React, { forwardRef, useEffect, useState, useRef } from "react";
import { motion, useAnimation } from "framer-motion";

const Alianzas = forwardRef((props, ref) => {
  const sectionRef = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        setVisible(entry.isIntersecting);
      },
      { threshold: 0.4 }
    );

    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const stats = [
    { value: 40, label: "CLIENTES" },
    { value: 1000, label: "HORAS DE CONSULTORÍA" },
    { value: 200, label: "BÚSQUEDAS" },
  ];

  return (
    <section
      ref={sectionRef}
      id="alianzas"
      className="py-32 px-6 bg-neutral text-primary flex flex-col items-center justify-center relative"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 1400 200"
        className="absolute inset-0 w-full h-full pointer-events-none z-0"
      >
        {visible && (
          <>
            {[
              "M 100 110 C 350 20, 1050 20, 1300 110",
              "M 100 90 C 400 0, 1000 0, 1300 90",
              "M 100 130 C 300 50, 1100 50, 1300 130",
            ].map((d, i) => (
              <motion.path
                key={i}
                d={d}
                fill="transparent"
                stroke={`url(#gradient-${i})`}
                strokeWidth={i === 0 ? 4 : i === 1 ? 2.5 : 2}
                strokeOpacity={0.5 - i * 0.1}
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{
                  duration: 2.5,
                  delay: i * 0.4,
                  ease: "easeInOut",
                }}
              />
            ))}

            <defs>
              {[0, 1, 2].map((i) => (
                <linearGradient key={i} id={`gradient-${i}`} x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#120B3E" />
                  <stop offset="50%" stopColor="#AB65AD" />
                  <stop offset="100%" stopColor="#120B3E" />
                </linearGradient>
              ))}
            </defs>
          </>
        )}
      </svg>

      <div className="flex flex-col sm:flex-row justify-center items-center gap-y-10 sm:gap-y-0 sm:gap-x-20 md:gap-x-36 relative z-10">
        {stats.map((stat, idx) => (
          <AnimatedCircle
            key={idx}
            targetValue={stat.value}
            label={stat.label}
            visible={visible}
            delay={idx * 0.3}
          />
        ))}
      </div>
    </section>
  );
});

const AnimatedCircle = ({ targetValue, label, visible, delay }) => {
  const [count, setCount] = useState(0);
  const controls = useAnimation();

  useEffect(() => {
    if (visible) {
      controls.start({
        opacity: 1,
        y: 0,
        transition: { duration: 0.8, delay },
      });
    } else {
      controls.start({ opacity: 0, y: 8 });
    }
  }, [visible, controls, delay]);

  useEffect(() => {
    if (!visible) return;

    setCount(0);
    const duration = 2000;
    const frameDuration = 16;
    const totalFrames = Math.round(duration / frameDuration);
    let frame = 0;

    const easeOutQuad = (t) => t * (2 - t);

    const counter = setInterval(() => {
      frame++;
      const progress = easeOutQuad(frame / totalFrames);
      const currentCount = Math.round(targetValue * progress);
      setCount(currentCount);

      if (frame >= totalFrames) {
        clearInterval(counter);
      }
    }, frameDuration);

    return () => clearInterval(counter);
  }, [visible, targetValue]);

  return (
    <motion.div
      className="flex flex-col items-center justify-center text-center group opacity-0 translate-y-8"
      animate={controls}
    >
      <div className="relative w-36 h-36 sm:w-44 sm:h-44 md:w-52 md:h-52 flex items-center justify-center">
        <div className="absolute inset-0 rounded-full bg-[#120B3E] p-[3px]">
          <div className="w-full h-full rounded-full bg-[#120B3E] flex items-center justify-center backdrop-blur-md shadow-2xl transition-all duration-500 group-hover:shadow-[#120B3E]/40 group-hover:scale-105 group-hover:brightness-110">
            <div className="relative w-[90%] h-[90%] rounded-full bg-[#120B3E]/20 backdrop-blur-xl border border-white/20 shadow-inner flex items-center justify-center transition-all duration-500 group-hover:bg-[#120B3E]/30">
              <span className="text-2xl sm:text-4xl md:text-5xl font-extrabold text-white tracking-tight drop-shadow-lg">
                +{count}
              </span>
              <div className="absolute inset-0 rounded-full bg-gradient-to-t from-transparent via-[#120B3E]/20 to-[#D1D1CF]/10 opacity-40 blur-md"></div>
            </div>
          </div>
        </div>
      </div>

      <p
        className="mt-4 sm:mt-6 text-center text-lg sm:text-xl md:text-2xl font-semibold tracking-wider leading-snug max-w-xs md:max-w-[180px]"
        style={{ color: "#120B3E", fontFamily: "CocoGose, sans-serif" }}
      >
        {label}
      </p>
    </motion.div>
  );
};

export default Alianzas;
